/**
 * JanusClient — Browser-side WebSocket + WebRTC client for Janus AudioBridge.
 *
 * Connects the agent's browser to a Janus AudioBridge room so they can
 * hear and speak with the WhatsApp caller. The audio path is:
 *
 *   WhatsApp (Meta) <--WebRTC--> Janus AudioBridge <--WebRTC--> Agent Browser
 *
 * Usage:
 *   const client = new JanusClient('ws://187.77.43.114:8188');
 *   await client.connect();
 *   const stream = await client.joinRoom(roomId);
 *   // stream is a MediaStream with remote audio
 *   audioElement.srcObject = stream;
 *   // later:
 *   client.disconnect();
 */

const KEEP_ALIVE_MS = 25_000;
const TRANSACTION_TIMEOUT_MS = 10_000;
const RECONNECT_MAX_ATTEMPTS = 3;
const RECONNECT_BASE_DELAY_MS = 1_000; // 1s, 2s, 4s

/**
 * Used when the API does not supply `iceServers` — i.e. TURN is off. Janus sits
 * on a public IP with nat_1_1_mapping so STUN alone suffices on a normal
 * network; on a network that blocks UDP it is not enough, which is what the
 * TURN relay from the API is for.
 */
const DEFAULT_ICE_SERVERS = [{ urls: "stun:stun.l.google.com:19302" }];

export default class JanusClient {
  /**
   * @param {string} wsUrl - Janus WebSocket URL (ws://host:port)
   * @param {object} [opts]
   * @param {string} [opts.apiSecret] - Janus API secret / stored token for auth
   * @param {RTCIceServer[]} [opts.iceServers] - ICE servers from the API, which
   *   include a TURN relay when it is enabled. Stored on the instance so the
   *   reconnection path (`_rejoinRoom`) uses the exact same set — otherwise the
   *   audio would come back STUN-only after an F5 and die on a restrictive
   *   network, with nothing linking cause to symptom.
   * @param {function} [opts.onReconnecting] - Called when reconnection starts
   * @param {function} [opts.onReconnected] - Called when reconnection succeeds
   * @param {function} [opts.onReconnectFailed] - Called when all retries exhausted
   */
  constructor(
    wsUrl,
    {
      apiSecret,
      iceServers,
      onReconnecting,
      onReconnected,
      onReconnectFailed,
    } = {},
  ) {
    this.wsUrl = wsUrl;
    /** @type {string|null} */
    this.apiSecret = apiSecret || null;
    /** @type {RTCIceServer[]} */
    this.iceServers = iceServers?.length ? iceServers : DEFAULT_ICE_SERVERS;
    /** @type {WebSocket|null} */
    this.ws = null;
    /** @type {number|null} */
    this.sessionId = null;
    /** @type {number|null} */
    this.handleId = null;
    /** @type {RTCPeerConnection|null} */
    this.pc = null;
    /** @type {MediaStream|null} */
    this.localStream = null;
    /** @type {MediaStream|null} */
    this.remoteStream = null;
    /** @type {Map<string, {resolve, reject, timer, waitForEvent}>} */
    this.pendingTransactions = new Map();
    /** @type {number|null} */
    this.keepAliveTimer = null;
    /** @type {number|null} */
    this.roomId = null;
    /** @type {boolean} */
    this.muted = false;

    // Reconnection state
    this._intentionalDisconnect = false;
    this._reconnectAttempt = 0;
    this._reconnectTimer = null;

    // Callbacks
    this._onReconnecting = onReconnecting || null;
    this._onReconnected = onReconnected || null;
    this._onReconnectFailed = onReconnectFailed || null;

    this._onMessage = this._onMessage.bind(this);
  }

  // ════════════════════════════════════════
  // CONNECTION
  // ════════════════════════════════════════

  /**
   * Connect to Janus Gateway via WebSocket, create session + handle.
   */
  async connect() {
    if (this.ws?.readyState === WebSocket.OPEN && this.sessionId) {
      return;
    }

    await this._wsConnect();
    this.sessionId = await this._createSession();
    this._startKeepAlive();
    this.handleId = await this._attachPlugin("janus.plugin.audiobridge");

    console.log(
      "[JanusClient] Connected. Session:",
      this.sessionId,
      "Handle:",
      this.handleId,
    );
  }

  _wsConnect() {
    return new Promise((resolve, reject) => {
      console.log("[JanusClient] Connecting to", this.wsUrl);
      this.ws = new WebSocket(this.wsUrl, "janus-protocol");

      const timeout = setTimeout(() => {
        this.ws?.close();
        reject(new Error("Janus WS connection timeout"));
      }, TRANSACTION_TIMEOUT_MS);

      this.ws.onopen = () => {
        clearTimeout(timeout);
        console.log("[JanusClient] WebSocket connected");
        this.ws.onmessage = this._onMessage;
        resolve();
      };

      this.ws.onerror = (err) => {
        clearTimeout(timeout);
        console.error("[JanusClient] WebSocket error:", err);
        reject(new Error("Janus WS connection error"));
      };

      this.ws.onclose = (ev) => {
        console.warn("[JanusClient] WebSocket closed:", ev.code, ev.reason);
        this._handleUnexpectedClose();
      };
    });
  }

  /**
   * Disconnect everything: WebRTC + WebSocket.
   */
  disconnect() {
    console.log("[JanusClient] Disconnecting...");
    this._intentionalDisconnect = true;

    // Cancel any pending reconnection
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    // Stop local media tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
    }

    // Close PeerConnection
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    this.remoteStream = null;

    // Destroy session on Janus
    if (this.ws?.readyState === WebSocket.OPEN && this.sessionId) {
      try {
        const msg = {
          janus: "destroy",
          session_id: this.sessionId,
          transaction: this._txnId(),
        };
        if (this.apiSecret) msg.token = this.apiSecret;
        this.ws.send(JSON.stringify(msg));
      } catch {
        /* ignore */
      }
    }

    this._cleanup();
  }

  /**
   * Handle unexpected WebSocket close — trigger reconnection if we were in a room.
   */
  _handleUnexpectedClose() {
    // Clean WS/session state but preserve localStream, roomId, muted for reconnect
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }

    for (const [, p] of this.pendingTransactions) {
      clearTimeout(p.timer);
      p.reject(new Error("Janus connection closed"));
    }
    this.pendingTransactions.clear();

    if (this.ws) {
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN) this.ws.close();
      this.ws = null;
    }

    this.sessionId = null;
    this.handleId = null;

    // Close existing PeerConnection (will be recreated on reconnect)
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }

    // Only attempt reconnect if we were in a room and disconnect wasn't intentional
    if (!this._intentionalDisconnect && this.roomId) {
      this._attemptReconnect();
    }
  }

  /**
   * Attempt to reconnect with exponential backoff.
   */
  _attemptReconnect() {
    if (this._reconnectAttempt >= RECONNECT_MAX_ATTEMPTS) {
      console.error(
        "[JanusClient] Reconnection failed after",
        RECONNECT_MAX_ATTEMPTS,
        "attempts",
      );
      this._onReconnectFailed?.();
      this._reconnectAttempt = 0;
      // Full cleanup — stop mic, clear room
      if (this.localStream) {
        this.localStream.getTracks().forEach((t) => t.stop());
        this.localStream = null;
      }
      this.roomId = null;
      return;
    }

    const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, this._reconnectAttempt);
    this._reconnectAttempt++;

    console.log(
      `[JanusClient] Reconnecting in ${delay}ms (attempt ${this._reconnectAttempt}/${RECONNECT_MAX_ATTEMPTS})`,
    );
    this._onReconnecting?.(this._reconnectAttempt);

    this._reconnectTimer = setTimeout(async () => {
      this._reconnectTimer = null;
      try {
        // Re-establish WS + session + handle
        await this._wsConnect();
        this.sessionId = await this._createSession();
        this._startKeepAlive();
        this.handleId = await this._attachPlugin("janus.plugin.audiobridge");

        // Re-join room with existing localStream (mic already acquired)
        if (!this.localStream) {
          this.localStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
            video: false,
          });
        }

        const stream = await this._rejoinRoom(this.roomId);

        // Success — reset counter
        this._reconnectAttempt = 0;
        console.log("[JanusClient] Reconnected successfully");
        this._onReconnected?.(stream);
      } catch (err) {
        console.warn("[JanusClient] Reconnect attempt failed:", err.message);
        this._attemptReconnect();
      }
    }, delay);
  }

  /**
   * Creates the RTCPeerConnection and wires every handler.
   *
   * Both entry points use this — `joinRoom` (first join) and `_rejoinRoom`
   * (reconnection after an F5 or a network blip). They used to build the
   * connection separately, so any divergence between them changed how a call
   * behaved *after* a reconnect. `iceServers` reaching only one of them is that
   * bug with the nastiest symptom: audio dies mid-conversation on a restrictive
   * network and nobody links it to the reconnection.
   *
   * Requires `this.localStream` to already hold the microphone.
   *
   * @returns {RTCPeerConnection}
   */
  _buildPeerConnection() {
    const rtcConfig = { iceServers: this.iceServers };

    // Interruptor de diagnostico: descarta os caminhos diretos e obriga a midia
    // a passar pelo relay. Serve para (a) validar que o TURN entrega audio sem
    // precisar estar numa rede restritiva, e (b) confirmar a hipotese quando um
    // atendente especifico reclama de chamada muda -- se com relay funciona, o
    // problema e a rede dele bloqueando UDP.
    // Ativar no console do atendente:  localStorage.setItem('tw:forceRelay','1')
    try {
      if (localStorage.getItem("tw:forceRelay") === "1") {
        rtcConfig.iceTransportPolicy = "relay";
        console.warn(
          "[JanusClient] tw:forceRelay ativo - so candidatos relay serao usados",
        );
      }
    } catch {
      // localStorage indisponivel (aba privada, storage bloqueado) - ignora
    }

    this.pc = new RTCPeerConnection(rtcConfig);

    for (const track of this.localStream.getAudioTracks()) {
      this.pc.addTrack(track, this.localStream);
    }

    this.remoteStream = new MediaStream();
    this.pc.ontrack = (ev) => {
      console.log("[JanusClient] Remote track received:", ev.track.kind);
      this.remoteStream.addTrack(ev.track);
    };

    // Janus uses trickle ICE
    this.pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        this._send({
          janus: "trickle",
          session_id: this.sessionId,
          handle_id: this.handleId,
          candidate: {
            candidate: ev.candidate.candidate,
            sdpMid: ev.candidate.sdpMid,
            sdpMLineIndex: ev.candidate.sdpMLineIndex,
          },
        }).catch((err) =>
          console.warn("[JanusClient] Trickle error:", err.message),
        );
      } else {
        // No more candidates — send completion
        this._send({
          janus: "trickle",
          session_id: this.sessionId,
          handle_id: this.handleId,
          candidate: { completed: true },
        }).catch(() => {});
      }
    };

    this.pc.oniceconnectionstatechange = () => {
      console.log("[JanusClient] ICE state:", this.pc?.iceConnectionState);
    };

    this.pc.onconnectionstatechange = () => {
      console.log("[JanusClient] Connection state:", this.pc?.connectionState);
    };

    return this.pc;
  }

  /**
   * Re-join a room with existing localStream (used during reconnection).
   * Same as joinRoom but skips getUserMedia since we already have the mic.
   */
  async _rejoinRoom(roomId) {
    this._buildPeerConnection();

    const offer = await this.pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    });
    await this.pc.setLocalDescription(offer);

    const response = await this._pluginMessage(
      { request: "join", room: roomId, display: "Agent" },
      { type: "offer", sdp: offer.sdp },
    );

    const sdpAnswer = response.jsep?.sdp;
    if (!sdpAnswer)
      throw new Error("Janus did not return SDP answer on rejoin");

    await this.pc.setRemoteDescription({ type: "answer", sdp: sdpAnswer });

    // Restore mute state
    if (this.muted) {
      for (const track of this.localStream.getAudioTracks()) {
        track.enabled = false;
      }
      this._pluginMessage({ request: "configure", muted: true }).catch(
        () => {},
      );
    }

    return this.remoteStream;
  }

  _cleanup() {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }

    for (const [, p] of this.pendingTransactions) {
      clearTimeout(p.timer);
      p.reject(new Error("Janus connection closed"));
    }
    this.pendingTransactions.clear();

    if (this.ws) {
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN) this.ws.close();
      this.ws = null;
    }

    this.sessionId = null;
    this.handleId = null;
    this.roomId = null;
  }

  _startKeepAlive() {
    this.keepAliveTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.sessionId)
        return;
      const msg = {
        janus: "keepalive",
        session_id: this.sessionId,
        transaction: this._txnId(),
      };
      if (this.apiSecret) {
        msg.token = this.apiSecret;
      }
      this.ws.send(JSON.stringify(msg));
    }, KEEP_ALIVE_MS);
  }

  // ════════════════════════════════════════
  // MESSAGING
  // ════════════════════════════════════════

  _txnId() {
    return `txn_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  }

  /**
   * Send a message and wait for matching response.
   */
  _send(msg, { waitForEvent = false } = {}) {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        return reject(new Error("WS not connected"));
      }

      const transaction = msg.transaction || this._txnId();
      msg.transaction = transaction;

      const timer = setTimeout(() => {
        this.pendingTransactions.delete(transaction);
        reject(new Error(`Janus timeout: ${msg.janus}`));
      }, TRANSACTION_TIMEOUT_MS);

      // Include auth token for Janus stored-token auth
      if (this.apiSecret) {
        msg.token = this.apiSecret;
      }

      this.pendingTransactions.set(transaction, {
        resolve,
        reject,
        timer,
        waitForEvent,
      });
      this.ws.send(JSON.stringify(msg));
    });
  }

  _onMessage(ev) {
    let data;
    try {
      data = JSON.parse(ev.data);
    } catch {
      return;
    }

    const txn = data.transaction;
    if (txn && this.pendingTransactions.has(txn)) {
      const p = this.pendingTransactions.get(txn);

      // For plugin messages, skip 'ack' and wait for 'event'
      if (data.janus === "ack" && p.waitForEvent) {
        return;
      }

      this.pendingTransactions.delete(txn);
      clearTimeout(p.timer);

      if (data.janus === "error") {
        p.reject(new Error(data.error?.reason || "Janus error"));
      } else {
        p.resolve(data);
      }
    }
  }

  // ════════════════════════════════════════
  // SESSION & HANDLE
  // ════════════════════════════════════════

  async _createSession() {
    const res = await this._send({ janus: "create" });
    return res.data?.id;
  }

  async _attachPlugin(plugin) {
    const res = await this._send({
      janus: "attach",
      session_id: this.sessionId,
      plugin,
    });
    return res.data?.id;
  }

  /**
   * Send a plugin message (body + optional jsep).
   */
  async _pluginMessage(body, jsep = null) {
    const msg = {
      janus: "message",
      session_id: this.sessionId,
      handle_id: this.handleId,
      body,
    };
    if (jsep) msg.jsep = jsep;
    return this._send(msg, { waitForEvent: true });
  }

  // ════════════════════════════════════════
  // JOIN ROOM (WebRTC)
  // ════════════════════════════════════════

  /**
   * Join a Janus AudioBridge room and establish WebRTC audio.
   * Returns a MediaStream with the remote audio (WhatsApp caller).
   *
   * @param {number} roomId - AudioBridge room ID to join
   * @returns {Promise<MediaStream>} Remote audio stream
   */
  async joinRoom(roomId) {
    this.roomId = roomId;

    // 1. Get microphone access
    console.log("[JanusClient] Requesting microphone access...");
    this.localStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 48000,
      },
      video: false,
    });
    console.log("[JanusClient] Microphone acquired");

    // 2. Create PeerConnection (same builder the reconnection path uses)
    this._buildPeerConnection();

    // 3. Create offer
    const offer = await this.pc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    });
    await this.pc.setLocalDescription(offer);

    console.log("[JanusClient] Joining room", roomId, "with WebRTC offer");

    // 4. Send join + offer to Janus AudioBridge
    const response = await this._pluginMessage(
      {
        request: "join",
        room: roomId,
        display: "Agent",
      },
      { type: "offer", sdp: offer.sdp },
    );

    // 5. Apply Janus SDP answer
    const sdpAnswer = response.jsep?.sdp;
    if (!sdpAnswer) {
      throw new Error("Janus did not return SDP answer for agent");
    }

    console.log(
      "[JanusClient] Got SDP answer from Janus, setting remote description",
    );
    await this.pc.setRemoteDescription({
      type: "answer",
      sdp: sdpAnswer,
    });

    console.log(
      "[JanusClient] WebRTC established! Agent is in AudioBridge room",
      roomId,
    );

    return this.remoteStream;
  }

  // ════════════════════════════════════════
  // CONTROLS
  // ════════════════════════════════════════

  /**
   * Mute/unmute the agent's microphone.
   * @param {boolean} muted
   */
  setMuted(muted) {
    this.muted = muted;
    if (this.localStream) {
      for (const track of this.localStream.getAudioTracks()) {
        track.enabled = !muted;
      }
    }
    // Also tell Janus AudioBridge to mute (stops mixing our audio)
    if (this.handleId) {
      this._pluginMessage({ request: "configure", muted }).catch(() => {});
    }
  }
}
